import useAxios from 'axios-hooks';
import {
  format,
  parse,
  endOfMonth,
  startOfMonth,
  startOfWeek,
  startOfToday,
  isSameWeek,
  endOfWeek,
  // eslint-disable-next-line import/no-duplicates
} from 'date-fns';
import { filter, find, flatMap, union } from 'lodash-es';
import React, { useEffect, useMemo, useState } from 'react';
import { useField, useFormState } from 'react-final-form';
import {
  MasterSelect,
  NotFoundAlert,
  StepSelectButton,
  WarningBox,
} from 'components';
import { DateTimePicker } from 'formComponents/DateTimePicker';
import {
  DatesData,
  FormValues,
  ServiceGroupsInfo,
  MasterInfo,
  FirstStepType,
  IntervalsData,
  SlotsPerMaster,
} from 'types';
import { getServicesDurationWithMaxHoldAfter, parseTimeSlots } from 'utils';
import { Box, Divider, useMediaQuery } from '@chakra-ui/react';
import {
  LOCAL_TIMEZONE,
  SERVER_DATE_FORMAT,
  TIMEZONE_INFO,
} from 'constants/dates';
// eslint-disable-next-line import/no-duplicates
import ru from 'date-fns/locale/ru';

const initDayValue = (initialDate: Date | null) => {
  if (initialDate) {
    const inputDate = new Date(initialDate);
    const [year, month, date] = [
      inputDate.getFullYear(),
      inputDate.getMonth(),
      inputDate.getDate(),
    ];
    return new Date(year, month, date, 0, 0, 0);
  }

  return null;
};

const parseDates = (dates: string[]) =>
  dates.map((date) => parse(date, SERVER_DATE_FORMAT, new Date()));

export const TimeSlot: React.FC<{
  name: string;
  serviceGroups: ServiceGroupsInfo;
  possibleMastersIds?: string[];
  masters: MasterInfo[];
  dayOnly?: boolean;
  timeOnly?: boolean;
  hasRecordingFromService?: boolean;
  hasRecordingFromMaster?: boolean;
  setSecondStep?: (step: FirstStepType) => void;
  setSlotsPerMaster?: (slotsPerMaster: SlotsPerMaster[]) => void;
  setIntervalsFavor?: (intervalsFavor: IntervalsData) => void;
  setPossibleMastersByDayIds: (ids?: string[]) => void;
  specialistCases: {
    specialistNominativeCase: string;
    specialistGenitiveCase: string;
    specialistPrepositionalCase: string;
  };
  divisionId: string | null;
  timezone?: string;
  serviceSelected?: boolean;
  nameButtonMaster?: string;
  nameButtonService?: string;
}> = ({
  name,
  serviceGroups,
  possibleMastersIds,
  masters,
  dayOnly = false,
  timeOnly = false,
  hasRecordingFromService,
  hasRecordingFromMaster,
  setSecondStep,
  setSlotsPerMaster,
  setIntervalsFavor,
  setPossibleMastersByDayIds,
  specialistCases,
  divisionId,
  timezone,
  serviceSelected = false,
  nameButtonMaster,
  nameButtonService,
}) => {
  const {
    values: { widgetId, favorIds, favorCounts, employeeId },
  } = useFormState<FormValues>();

  const { input } = useField<Date | null>(name);
  const [dateChosen, setDateChosen] = useState<boolean>(false);
  const dayValue = useField('day').input.value;
  const [day, setDay] = React.useState<Date | null>(() =>
    initDayValue(input.value || dayValue),
  );
  const pickDate = day ? format(day, SERVER_DATE_FORMAT) : '';
  const today = new Date();
  const [isMobileDevice] = useMediaQuery('(max-width: 735px)');
  const [isWeekView, setWeekView] = useState(isMobileDevice);
  const [selectedPeriod, setSelectedPeriod] = useState(input.value || today);

  const periodStart = (() => {
    if (isWeekView) {
      return !isSameWeek(selectedPeriod, today, {
        locale: ru,
        weekStartsOn: 1,
      }) ||
        selectedPeriod.getMonth() !== today.getMonth() ||
        selectedPeriod.getFullYear() !== today.getFullYear()
        ? startOfWeek(selectedPeriod, { locale: ru, weekStartsOn: 1 })
        : startOfToday();
    }
    return selectedPeriod.getMonth() !== today.getMonth() ||
      selectedPeriod.getFullYear() !== today.getFullYear()
      ? startOfMonth(selectedPeriod)
      : startOfToday();
  })();
  const periodEnd = isWeekView
    ? endOfWeek(selectedPeriod, { locale: ru, weekStartsOn: 1 })
    : endOfMonth(selectedPeriod);

  const employeeIds = useMemo(() => {
    if (employeeId === 'any')
      return filter(possibleMastersIds, (id) =>
        find(masters, (master) => master.id === id && master.anyEmployee),
      );
    if (!employeeId) return possibleMastersIds;
    return [employeeId];
  }, [employeeId, masters, possibleMastersIds]);

  const [
    {
      data: datesData,
      // data: datesData = datesMock.dates.filter((d) => intersection(d.employeeIds, employeeIds)?.length),
      loading: datesLoading,
      error: datesError,
    },
  ] = useAxios<DatesData>(
    {
      url: '/booking-widget/client/schedule/work-days',
      method: 'POST',
      data: {
        widgetId,
        employeeIds,
        favorIds,
        duration: getServicesDurationWithMaxHoldAfter(
          serviceGroups,
          favorIds,
          favorCounts,
        ),
        startDate: timeOnly
          ? pickDate
          : format(periodStart, SERVER_DATE_FORMAT),
        endDate: timeOnly ? pickDate : format(periodEnd, SERVER_DATE_FORMAT),
        timeZone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(divisionId ? { divisionId } : {}),
      },
    },

    {
      manual: !employeeIds?.length,
    },
  );

  const dates = useMemo(
    () =>
      parseDates(
        datesData?.reduce((arrayDates: string[], currentData) => {
          return union(arrayDates, [currentData.date]);
        }, []) || [],
      ),
    [datesData],
  );

  useEffect(() => {
    if (day && datesData && dayOnly) {
      const data = datesData.find((item) => item.date === pickDate);
      setPossibleMastersByDayIds(data?.employeeTimes?.map(({ id }) => id));
    }
  }, [datesData, day, dayOnly, pickDate, setPossibleMastersByDayIds]);

  // Получение интервалов недоступности услуг выполняется, когда пользователь
  // нажал на кнопку "Выбрать услугу" или "Выбрать мастера" на экране "Выбор даты" в ветке "Запись с даты"
  // (теоретически можно перенести в Service - пока используется только там)
  const [
    {
      data: intervalsData,
      // data: intervalsData = intervalsMock,
      loading: intervalsLoading,
      error: intervalsError,
    },
  ] = useAxios<IntervalsData>(
    {
      url: '/booking-widget/client/widget/favor/unavailability-intervals',
      method: 'POST',
      data: {
        widgetId,
        favorIds: flatMap(serviceGroups).map((service) => service.id),
        date: day && pickDate,
        timeZone: timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...(divisionId ? { divisionId } : {}),
      },
    },
    {
      manual: !dateChosen,
    },
  );

  const timesData =
    day && find(datesData, ({ date }) => date === pickDate)?.employeeTimes;

  const times =
    timesData?.reduce((arrayTimes: string[], currentData) => {
      return union(arrayTimes, currentData.times);
    }, []) || [];

  // Если выбор с даты: дожидаемся загрузки интервалов недоступности и сохраняем выбранный день
  useEffect(() => {
    if (dateChosen && intervalsData) {
      setDateChosen(false);
      input.onChange(day);
    }
  }, [dateChosen, day, input, intervalsData]);

  useEffect(() => {
    if (timesData && input.value) {
      if (setSlotsPerMaster)
        setSlotsPerMaster(
          timesData.map((item) => ({
            employeeId: item.id,
            times: item.times,
          })),
        );
    }
  }, [input.value, setSlotsPerMaster, timesData]);

  useEffect(() => {
    if (intervalsData) {
      if (setIntervalsFavor) setIntervalsFavor(intervalsData);
    }
  }, [intervalsData]);

  const handleStepButtonClick = (step: FirstStepType) => () => {
    if (setSecondStep) setSecondStep(step);
    setDateChosen(true);
  };

  return !(dayValue && !datesLoading && !times.length) ? (
    <div>
      {!dayOnly && (
        <MasterSelect
          name="employeeId"
          masters={masters}
          possibleMastersIds={possibleMastersIds}
          serviceGroups={serviceGroups}
          widgetId={widgetId}
          isPossibleMastersExist={typeof possibleMastersIds !== 'undefined'}
          specialistCases={specialistCases}
          serviceSelected={serviceSelected}
        />
      )}
      {timezone && timezone !== LOCAL_TIMEZONE && (
        <WarningBox mb={4} message={TIMEZONE_INFO} />
      )}
      <DateTimePicker
        day={day}
        setDay={setDay}
        datesLoading={datesLoading || intervalsLoading}
        datesError={datesError || intervalsError}
        includeDays={dates}
        timesLoading={datesLoading}
        timesError={datesError}
        includeTimes={parseTimeSlots(times)}
        name={name}
        onMonthChange={setSelectedPeriod}
        dayOnly={dayOnly}
        slotOnly={!!dayValue}
        isWeekView={isWeekView}
        setWeekView={setWeekView}
      />
      <Divider orientation="horizontal" visibility={{ md: 'hidden' }} />
      <Box textAlign="center" mt={{ base: '4', md: '100px' }}>
        {(hasRecordingFromService || (dayOnly && !hasRecordingFromMaster)) && (
          <StepSelectButton
            variant="outline"
            width={{ base: '100%', md: '268px' }}
            mr={{ md: 8 }}
            onClick={handleStepButtonClick('service')}
            disabled={!day}
          >
            {nameButtonService}
          </StepSelectButton>
        )}
        {hasRecordingFromMaster && (
          <StepSelectButton
            variant="outline"
            width={{ base: '100%', md: '268px' }}
            mt={{ base: 2, md: 0 }}
            mr={{ md: 8 }}
            onClick={handleStepButtonClick('master')}
            disabled={!day}
          >
            {nameButtonMaster}
          </StepSelectButton>
        )}
      </Box>
    </div>
  ) : (
    <NotFoundAlert text="Нет доступных интервалов для записи" />
  );
};
